import React, { useState, useMemo, useContext } from "react";
import ReactDOM from "react-dom";
import { auth } from "../firebase";
import { NotificationsContext } from "../hooks/notificationsContext";

export default function FuturesPurchaseModal({
  companyStockPrice = 0,
  userRank,
  userState,
  companyState,
  onClose,
  globalTaxRate = 5
}) {
  const { addNotification } = useContext(NotificationsContext);

  const [stake, setStake] = useState("");
  const [target, setTarget] = useState("");
  const [direction, setDirection] = useState("Up");
  const [duration, setDuration] = useState("24Hr");
  const [simAccuracy, setSimAccuracy] = useState(100);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const stakeVal = Number(stake) || 0;
  const targetPct = Number(target) || 0;

  const formatMoney = (num = 0) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);

  const maxStake = userState?.userMoney || 0;
  const amountDue = stakeVal;

  const strikePrice = useMemo(() =>
    direction === "Up"
      ? companyStockPrice + companyStockPrice * (targetPct / 100)
      : companyStockPrice - companyStockPrice * (targetPct / 100)
  , [companyStockPrice, targetPct, direction]);

  const basePayout = useMemo(() => {
    if (!stakeVal || !targetPct) return 0;
    let predictionMod = 1;
    if (duration === "24Hr") predictionMod += (targetPct / 100) * 11;
    else if (duration === "7D") predictionMod += (targetPct / 100) * 17;
    else if (duration === "30D") predictionMod += (targetPct / 100) * 40;
    return predictionMod * stakeVal;
  }, [stakeVal, targetPct, duration]);

  const accuracyThreshold = 0.25;
  const decayRate = 2;

  const priceDiffPercent = useMemo(() => (100 - simAccuracy) / 100, [simAccuracy]);
  const isWithinWindow = priceDiffPercent <= accuracyThreshold;

  const simNetProfit = useMemo(() => {
    if (!stakeVal || !targetPct) return 0;

    if (!isWithinWindow) return 0;

    const accuracyMultiplier = Math.exp(-decayRate * priceDiffPercent);
    const grossPayout = basePayout * accuracyMultiplier;

    const taxableProfit = Math.max(0, grossPayout - stakeVal);
    const taxAmount = taxableProfit * (globalTaxRate / 100);
    const netPayout = grossPayout - taxAmount;

    return Math.round((netPayout - amountDue) * 100) / 100;
  }, [basePayout, priceDiffPercent, isWithinWindow, stakeVal, amountDue, globalTaxRate, targetPct]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch("https://transactionfutures-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          ticker: companyState.companyTicker,
          stake: stakeVal,
          duration,
          direction,
          target: targetPct
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Transaction failed.");
      addNotification(data.message, "success");
      onClose();
    } catch (err) {
      addNotification(err.message, "error");
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return ReactDOM.createPortal(
    <div style={{
      position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(5px)",
      display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000,
      fontFamily: "'JetBrains Mono', monospace"
    }}>
      <div style={{
        width: "440px", background: "rgba(0, 30, 30, 0.95)", border: "1px solid rgba(80, 227, 194, 0.3)",
        padding: "24px", position: "relative", boxShadow: "0 0 30px rgba(0,0,0,1)"
      }}>
        <h2 style={{ color: "#fff", margin: "0 0 20px 0", fontSize: "1.2rem", borderBottom: "1px solid rgba(80, 227, 194, 0.2)", paddingBottom: "10px" }}>
          Futures - {companyState?.companyTicker}
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "20px" }}>
          <div>
            <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "5px" }}>STAKE AMOUNT</label>
            <input 
              type="number" 
              value={stake} 
              onChange={e => setStake(Math.min(Number(e.target.value), maxStake))}
              style={{ 
                width: "100%", background: "rgba(0, 0, 0, 0.3)", border: "1px solid rgba(80, 227, 194, 0.2)", 
                color: "#fff", padding: "10px", boxSizing: "border-box", outline: "none", fontFamily: "inherit" 
              }}
            />
          </div>
          <div>
            <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "5px" }}>TARGET PCT (%)</label>
            <input 
              type="number" 
              value={target} 
              onChange={e => setTarget(e.target.value)}
              style={{ 
                width: "100%", background: "rgba(0, 0, 0, 0.3)", border: "1px solid rgba(80, 227, 194, 0.2)", 
                color: "#fff", padding: "10px", boxSizing: "border-box", outline: "none", fontFamily: "inherit" 
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: "20px" }}>
          <label style={{ color: "#888", fontSize: "0.6rem", display: "block", marginBottom: "8px" }}>PARAMETERS</label>
          <div style={{ display: "flex", gap: "5px", marginBottom: "8px" }}>
            {["Up", "Down"].map(d => (
              <button 
                key={d} onClick={() => setDirection(d)}
                style={{ 
                  flex: 1, padding: "8px", fontSize: "0.7rem", border: "1px solid #333",
                  background: direction === d ? "rgba(80, 227, 194, 0.2)" : "transparent",
                  color: direction === d ? "#50e3c2" : "#666", cursor: "pointer"
                }}
              >
                {d.toUpperCase()}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: "5px" }}>
            {["24Hr", "7D", "30D"].map(d => (
              <button 
                key={d} onClick={() => setDuration(d)}
                style={{ 
                  flex: 1, padding: "8px", fontSize: "0.7rem", border: "1px solid #333",
                  background: duration === d ? "rgba(80, 227, 194, 0.2)" : "transparent",
                  color: duration === d ? "#50e3c2" : "#666", cursor: "pointer"
                }}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: "rgba(0,0,0,0.5)", padding: "12px", border: "1px solid rgba(255,255,255,0.05)", marginBottom: "15px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px", fontSize: "0.7rem" }}>
              <span style={{ color: "#888" }}>STRIKE PRICE:</span>
              <span style={{ color: "#eee" }}>{formatMoney(strikePrice)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.7rem" }}>
              <span style={{ color: "#888" }}>TOTAL DUE:</span>
              <span style={{ color: "#ff7b72", fontWeight: "bold" }}>{formatMoney(amountDue)}</span>
            </div>
        </div>

        <div style={{ 
          marginBottom: "20px", padding: "12px", 
          background: !isWithinWindow ? "rgba(255, 123, 114, 0.05)" : "rgba(80, 227, 194, 0.03)", 
          border: `1px solid ${!isWithinWindow ? "rgba(255, 123, 114, 0.2)" : "rgba(80, 227, 194, 0.1)"}`,
          transition: "all 0.2s ease"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
            <label style={{ color: !isWithinWindow ? "#ff7b72" : "#50e3c2", fontSize: "0.6rem", letterSpacing: "0.05rem" }}>
              {!isWithinWindow ? "OUTSIDE ACCURACY WINDOW" : "ACCURACY SIMULATOR"}
            </label>
            <span style={{ color: !isWithinWindow ? "#ff7b72" : "#50e3c2", fontSize: "0.7rem", fontWeight: "bold" }}>
              {simAccuracy}% ACCURACY
            </span>
          </div>
          <input 
            type="range" min="0" max="100" value={simAccuracy}
            onChange={(e) => setSimAccuracy(Number(e.target.value))}
            style={{ width: "100%", accentColor: !isWithinWindow ? "#ff7b72" : "#50e3c2", cursor: "pointer", height: "2px", marginBottom: "12px" }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "8px" }}>
            <span style={{ color: "#888" }}>EST. NET PROFIT:</span>
            <span style={{ color: simNetProfit >= 0 ? "#50e3c2" : "#ff7b72", fontWeight: "bold" }}>
              {simNetProfit >= 0 ? "+" : ""}{formatMoney(simNetProfit)}
            </span>
          </div>
          <div style={{ color: !isWithinWindow ? "#ff7b72" : "#666", fontSize: "0.5rem", textAlign: "center", marginTop: "8px" }}>
            {!isWithinWindow 
              ? "CONTRACT FORFEIT" 
              : `YIELD: ${Math.round(Math.exp(-2 * priceDiffPercent) * 100)}% OF BASE PAYOUT`
            }
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onClose} style={{ flex: 1, padding: "12px", background: "transparent", border: "1px solid #333", color: "#888", cursor: "pointer", fontFamily: "inherit" }}>CANCEL</button>
          <button 
            onClick={() => setShowConfirm(true)}
            disabled={stakeVal < 1}
            style={{ flex: 2, padding: "12px", background: "#50e3c2", border: "none", color: "#000", fontWeight: "bold", cursor: "pointer", opacity: stakeVal < 1 ? 0.5 : 1 }}
          >
            PURCHASE
          </button>
        </div>

        {showConfirm && (
          <div style={{
            position: "absolute", inset: 0, background: "rgba(0, 20, 20, 0.98)", 
            display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", 
            padding: "20px", border: "2px solid #50e3c2", textAlign: "center", zIndex: 10
          }}>
            <div style={{ color: "#50e3c2", fontSize: "0.6rem", marginBottom: "10px" }}>CONTRACT CONFIRMATION</div>
            <div style={{ color: "#fff", marginBottom: "20px", fontSize: "0.8rem", lineHeight: "1.5" }}>
              Prediction: {direction.toUpperCase()} ({targetPct}%)<br/>
              Duration: {duration} | Stake: {formatMoney(stakeVal)}<br/>
            </div>
            <div style={{ display: "flex", gap: "10px", width: "100%" }}>
              <button onClick={() => setShowConfirm(false)} style={{ flex: 1, padding: "10px", background: "rgba(255,255,255,0.1)", color: "#fff", border: "none" }}>CANCEL</button>
              <button onClick={handleConfirm} style={{ flex: 1, padding: "10px", background: "#50e3c2", color: "#000", border: "none", fontWeight: "bold" }}>
                {loading ? "PROCESSING..." : "CONFIRM"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}